import { fetchABTestExperiment } from '@app/promises/abTesting';
import { KvadratInsights } from '@inter-ikea-kvadrat/insights-dpc-v5-adapter';
import { InsightsService } from '@inter-ikea-storageone/shared/analytics';
import { getMemoizedPromiseFactory } from '@inter-ikea-storageone/shared/utils/getMemoizedPromiseFactory';
import { getAnalyticsConsentStatus } from '@inter-ikea-storageone/shared/cookies/getCookieConsentLevel';
import { delay } from '@inter-ikea-storageone/shared/utils/delay';
import { isProduction } from '@inter-ikea-storageone/shared/utils/platform';

import type { IKvadrat } from '@inter-ikea-kvadrat/kvadrat';

const _getInsightsService = async () => {
  const abTestExperiment = await fetchABTestExperiment();

  const insightsService = new InsightsService({
    abTestExperiment
  });
  await insightsService.connect(); // important that insights service is connected before we start using the service
  return insightsService;
};

// getMemoizedPromiseFactory ensures that if we have multiple calls for getInsightsService() the callers
//   get back the same result without creating duplicate requests/responses
export const getInsightsService = getMemoizedPromiseFactory(_getInsightsService);

const connectKvadratInsights = async (kvadrat: IKvadrat, dev: boolean) => {
  const insightsService = await getInsightsService();
  // Set up insights subscriber for analytics
  const kvadratInsights = new KvadratInsights();
  if (insightsService.insightsApi.isEnabled) {
    kvadratInsights.connect(kvadrat, insightsService.insightsApi, dev);
  }
};

export const tryToConnectKvadratInsights = async (kvadrat: IKvadrat) => {
  const dev = !import.meta.env.PROD || !isProduction();
  try {
    await Promise.race([
      getAnalyticsConsentStatus().then(consentLevel => {
        if (consentLevel === 'ANALYTICS ALLOWED') {
          connectKvadratInsights(kvadrat, dev);
        }
      }),
      delay(2000).then(() =>
        Promise.reject(new Error('Failed to connect insights for kvadrat within 2 seconds'))
      )
    ]);
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Error connecting insights for kvadrat', error);
  }
};
